import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import categoryService from '../../services/category.service';
import type { Category } from '../../dto/category.dto';
import './styles/categories.css';
import './styles/categories.mobile.css';
import './styles/categories-content.mobile.css';
import './styles/categorybutton.css';
import LoadingSpinner from '../../../../shared/components/LodingSpiner/LodingSpiner';

interface CategoryItem {
  image: string;
  label: string;
  slug: string;
}

interface CategoriesProps {
  activeTab: 'materials' | 'services';
}

const Categories: React.FC<CategoriesProps> = ({ activeTab }) => {
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [, setAllCategories] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab === 'materials') {
      fetchCategories();
    } else {
      fetchServices();
    }
  }, [activeTab]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getCategoryTree();
      if (response.success) {
        const mainCategories = response.data
          .filter((cat: Category) => cat.isActive && !cat.parent && cat.name && cat.slug)
          .map((cat: Category) => ({
            image: cat.image || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868',
            label: cat.name || 'Unnamed Category',
            slug: cat.slug
          }));
        setAllCategories(mainCategories);
        setCategories(mainCategories);
      } else {
        setError('Failed to load categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getServiceOptions();
      if (response.success) {
        const mainServices = response.data
          .filter((srv: any) => srv.isActive && srv.name && (srv.slug || srv.id))
          .map((srv: any) => ({
            image: srv.img || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868',
            label: srv.name,
            slug: srv.slug || srv.id
          }));
        setCategories(mainServices);
      } else {
        setError('Failed to load services');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching services');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug: string) => {
    navigate(`/view-products/${slug}`);
  };

  return (
    <div className="categories-container">
      {loading ? (
        <div className={activeTab === 'materials' ? 'materials-section' : 'services-section'} style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <LoadingSpinner size="large" />
        </div>
      ) : error ? (
        <div className={activeTab === 'materials' ? 'materials-section' : 'services-section'}>
          <div className="error-message">{error}</div>
        </div>
      ) : activeTab === 'materials' ? (
        <>
          <div className="materials-section">
            <div className="materials-grid fixed-6-cols">
              {categories.map((category: CategoryItem) => (
                <div
                  key={category.slug}
                  className="material-card"
                  onClick={() => handleCategoryClick(category.slug)}
                >
                  <div className="card-icon">
                    <img
                      src={category.image}
                      alt={category.label || 'Category'}
                      className="card-image"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
                      }}
                    />
                  </div>
                  <span className="card-label">
                    {category.label || 'Unnamed'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="services-category-section">
          <div className="materials-grid fixed-6-cols">
            {categories.map((service: any) => {
              const imageUrl = service.image || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
              return (
                <div
                  key={service.slug || service.id}
                  className="material-card"
                >
                  <div className="card-icon">
                    <img
                      src={imageUrl}
                      alt={service.label || 'Service'}
                      className="card-image"
                    />
                  </div>
                  <span className="card-label">
                    {service.label || 'Unnamed'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;